import React, { useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';
import { LocalityHubSeoItem } from '../../types/seo';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (hub: LocalityHubSeoItem) => void | Promise<void>;
}

const escapeHtml = (value: string) => value.replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char] || char));

export default function CreateLocalityHubModal({ open, onClose, onCreated }: Props) {
  const [area, setArea] = useState('');
  const [projectSlug, setProjectSlug] = useState('');
  const [slug, setSlug] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [guide, setGuide] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleArea = (value: string) => {
    setArea(value);
    if (!slug) setSlug(value.toLowerCase().replace(/[^a-z0-9\s-]/g, '').trim().replace(/\s+/g, '-'));
  };

  const handleSave = async () => {
    if (![area, projectSlug, slug, title, description, guide].every((value) => value.trim())) {
      setError('Complete the area, related project, URL slug, title, description, and original local guide.');
      return;
    }
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
      setError('Use lowercase letters, numbers, and hyphens for the URL slug.');
      return;
    }
    setSaving(true);
    setError('');
    const now = new Date().toISOString();
    const richGuideHtml = guide.split(/\n\s*\n/).filter(Boolean).map((paragraph) => `<p>${escapeHtml(paragraph.trim())}</p>`).join('');
    const hub: LocalityHubSeoItem = {
      id: slug,
      slug,
      title,
      type: 'locality',
      microMarket: area,
      projectRef: projectSlug,
      activeListingsCount: 0,
      annualAppreciationRate: '',
      heroHeadline: title,
      heroSubheadline: description,
      seoTitle: title,
      metaDescription: description,
      richGuideHtml,
      faqs: [],
      schemaJson: { '@context': 'https://schema.org', '@type': 'Place', name: `${area}, Surat` },
      auditScore: 0,
      syncStatus: 'synced',
      lastSyncedAt: now,
      lastEditedAt: now,
    };
    try {
      await onCreated(hub);
      onClose();
    } catch {
      setError('The locality page could not be saved. Your text is still in this form.');
    } finally {
      setSaving(false);
    }
  };

  return <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>Create a locality page</DialogTitle>
    <DialogContent dividers>
      <Stack spacing={2}>
        <Alert severity="info">Use original, verified information about an area where a Swarna project is relevant. Do not add travel times, future infrastructure, prices, or legal claims without checking them.</Alert>
        <TextField label="Area name" value={area} onChange={(event) => handleArea(event.target.value)} required />
        <TextField label="Related project URL slug" value={projectSlug} onChange={(event) => setProjectSlug(event.target.value)} helperText="For example: swarna-avsar" required />
        <TextField label="Page URL slug" value={slug} onChange={(event) => setSlug(event.target.value)} helperText={`/localities/${slug || 'area-name'}`} required />
        <TextField label="Page title" value={title} onChange={(event) => setTitle(event.target.value)} required />
        <TextField label="Search description" value={description} onChange={(event) => setDescription(event.target.value)} multiline minRows={2} required />
        <TextField label="Original local guide" value={guide} onChange={(event) => setGuide(event.target.value)} multiline minRows={7} helperText="Explain the area, the project's location, buyer considerations, and facts your team has checked." required />
        {error && <Alert severity="error">{error}</Alert>}
      </Stack>
    </DialogContent>
    <DialogActions><Button onClick={onClose} disabled={saving}>Cancel</Button><Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save page to website database'}</Button></DialogActions>
  </Dialog>;
}
